import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Pane,
  TextInputField,
  Button,
  Card,
  Paragraph,
  Text,
  toaster
} from "evergreen-ui";
import { setprofile } from "../../redux/user/profileSlice.js";
import CloudImage from "../../components/CloudImage";
import ImageUpload from "../../components/ImageUpload";
import { useMutation } from "@apollo/client";
import UPDATE_USER_MUTATION from "../../graphql/mutations/UpdateUser.js";
import UPDATE_PUBLISHER_USER_MUTATION from "../../graphql/mutations/UpdatePublisherUser.js";

function AccountSettings() {
  const profile = useSelector((state) => state.profile.value);
  const userId = useSelector((state) => state.user.value);
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const allPublishers = useSelector((state) => state.pubusers?.value);
  const dispatch = useDispatch();
  const [firstName, setFirstName] = useState(profile?.firstName || "");
  const [lastName, setLastName] = useState(profile?.lastName || "");
  const [email, setEmail] = useState(profile?.email || "");
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const [updatePublisherUser] = useMutation(UPDATE_PUBLISHER_USER_MUTATION);

  useEffect(() => {
    // Log the full profile object to see its structure
    console.log("Profile object structure:", profile);
    console.log("All publishers:", allPublishers);

    if (profile) {
      setFirstName(profile.firstName || "");
      setLastName(profile.lastName || "");
      setEmail(profile.email || "");
    }
  }, [profile, allPublishers]);

  // Handle profile image upload using ImageUpload component
  // This receives the direct Firebase download URL from the updated ImageUpload component
  const handleProfileImageUpdate = (downloadURL) => {
    console.log('handleProfileImageUpdate received downloadURL:', downloadURL);
    if (downloadURL) {
      updateProfilePhoto(downloadURL);
    } else {
      console.error('No download URL received from ImageUpload component');
      toaster.danger('Failed to upload image. Please try again.');
    }
  };

  // Update the user profile with the new photo URL
  const updateProfilePhoto = async (downloadURL) => {
    try {
      setIsUploading(true);
      console.log('Starting updateProfilePhoto with URL:', downloadURL);

      // ADD THIS LOGGING
      console.log('DEBUG updateProfilePhoto: profile state:', JSON.stringify(profile, null, 2));
      console.log('DEBUG updateProfilePhoto: allPublishers state:', JSON.stringify(allPublishers, null, 2));
      console.log('DEBUG updateProfilePhoto: currentpublisher state:', JSON.stringify(currentpublisher, null, 2));
      console.log('DEBUG updateProfilePhoto: userId state:', userId);

      // Log complete profile to see what's available
      console.log('Complete profile object:', profile);
      console.log('User ID from Redux:', userId);
      console.log('Publisher ID from Redux:', currentpublisher?.id);
      console.log('All publishers from Redux:', allPublishers);

      // Get all available IDs that might be the PublisherUser ID
      const possibleIds = [];

      // Check all possible locations where the ID might be stored

      // 1. Check in profile.publisheruserSet (from QueryUser)
      if (profile?.publisheruserSet && profile.publisheruserSet.length > 0) {
        console.log("[AccountSettings] Processing profile.publisheruserSet:", JSON.stringify(profile.publisheruserSet, null, 2));
        for (const pubUser of profile.publisheruserSet) {
          console.log("[AccountSettings] Current pubUser from publisheruserSet:", JSON.stringify(pubUser, null, 2));
          if (pubUser.id) {
            if (pubUser.publisher && pubUser.publisher.id) {
              possibleIds.push({
                id: pubUser.id,
                source: 'publisheruserSet',
                publisherId: pubUser.publisher.id
              });
            } else {
              console.warn("[AccountSettings] pubUser from publisheruserSet is missing publisher.id:", JSON.stringify(pubUser, null, 2));
            }
          } else {
            console.warn("[AccountSettings] pubUser from publisheruserSet is missing a top-level id:", JSON.stringify(pubUser, null, 2));
          }
        }
      } else {
        console.log("[AccountSettings] profile.publisheruserSet is empty or undefined.");
      }

      // 2. Check in profile.userpublishers (from GlobalData query)
      if (profile?.userpublishers && profile.userpublishers.length > 0) {
        console.log("[AccountSettings] Processing profile.userpublishers:", JSON.stringify(profile.userpublishers, null, 2));
        for (const userPub of profile.userpublishers) {
          console.log("[AccountSettings] Current userPub from userpublishers:", JSON.stringify(userPub, null, 2));
          if (userPub.id && userPub.publisher && userPub.publisher.id) {
            possibleIds.push({
              id: userPub.id,
              source: 'userpublishers',
              publisherId: userPub.publisher.id
            });
          } else {
            console.warn("[AccountSettings] userPub from userpublishers is missing id or publisher.id:", JSON.stringify(userPub, null, 2));
          }
        }
      } else {
        console.log("[AccountSettings] profile.userpublishers is empty or undefined.");
      }

      // 3. Check in profile.publisherusers (from PublisherUsers query)
      if (profile?.publisherusers && profile.publisherusers.length > 0) {
        for (const pubUser of profile.publisherusers) {
          if (pubUser.id) {
            possibleIds.push({
              id: pubUser.id,
              source: 'publisherusers',
              publisherId: pubUser.publisher?.id
            });
          }
        }
      }

      // 4. Check if the ID is directly in the profile
      if (profile?.publisherUserId) {
        possibleIds.push({
          id: profile.publisherUserId,
          source: 'publisherUserId'
        });
      }

      // 5. Check if the ID is in the global state
      if (allPublishers && allPublishers.length > 0) {
        for (const pubUser of allPublishers) {
          if (pubUser.id) {
            possibleIds.push({
              id: pubUser.id,
              source: 'allpublishers',
              publisherId: pubUser.publisher?.id
            });
          }
        }
      }

      // 6. Hardcoded fallback based on the data you provided
      if (possibleIds.length === 0 && currentpublisher?.id === "1" && userId === "1") {
        possibleIds.push({
          id: "3",  // This is the ID from your provided data
          source: 'hardcoded_fallback',
          publisherId: "1"
        });
      }

      // Log all possible IDs
      console.log('Possible PublisherUser IDs:', possibleIds);

      // If we have multiple IDs, try to find the one that matches the current publisher
      let selectedId = null;

      if (possibleIds.length > 0) {
        // First, try to find an ID that matches the current publisher
        if (currentpublisher?.id) {
          const matchingId = possibleIds.find(item =>
            item.publisherId && item.publisherId.toString() === currentpublisher.id.toString()
          );

          if (matchingId) {
            selectedId = matchingId.id;
            console.log('Selected ID matching current publisher:', selectedId, 'from source:', matchingId.source);
          }
        }

        // If no matching ID found, just use the first one
        if (!selectedId) {
          selectedId = possibleIds[0].id;
          console.log('Using first available ID:', selectedId, 'from source:', possibleIds[0].source);
        }
      } else {
        console.error('No possible PublisherUser IDs found');
        console.error('This is likely because the profile object structure is different than expected.');
        console.error('Please check the Redux state and the GraphQL queries to ensure they return the correct data.');

        // Dump the full structure of profile and related objects to help diagnose the issue
        console.log('DEBUG - Full profile object:', JSON.stringify(profile, null, 2));
        console.log('DEBUG - Current publisher:', JSON.stringify(currentpublisher, null, 2));
        console.log('DEBUG - All publishers:', JSON.stringify(allPublishers, null, 2));
        console.log('DEBUG - User ID:', userId);

        // Try a fallback approach - update directly in the Redux store only
        try {
          // Create a deep copy of the profile to avoid direct state mutation
          const updatedProfile = JSON.parse(JSON.stringify(profile));
          // Update the top-level photoUrl
          updatedProfile.photoUrl = downloadURL;

          dispatch(setprofile(updatedProfile));
          toaster.warning('Profile photo updated in current session only. Changes may not persist after refresh.');
          setIsUploading(false);
          return;
        } catch (fallbackError) {
          console.error('Even fallback approach failed:', fallbackError);
          toaster.danger('Cannot update profile: No publisher user ID found. Please refresh the page or contact support.');
          setIsUploading(false);
          return;
        }
      }

      // Ensure the ID is an integer
      const parsedId = parseInt(selectedId, 10);
      if (isNaN(parsedId)) {
        console.error('Invalid publisher user ID (not a number):', selectedId);
        toaster.danger('Cannot update profile: Invalid ID format. Please refresh the page or contact support.');
        setIsUploading(false);
        return;
      }

      console.log('Using publisher user ID for update:', parsedId);

      // Use snake_case for backend database column names
      const payload = JSON.stringify([
        { key: "photo_url", val: downloadURL }
      ]);

      console.log('Mutation payload:', payload);

      // Update the publisher user record
      console.log('Sending GraphQL mutation with variables:', {
        id: parsedId,
        changed: payload
      });

      const result = await updatePublisherUser({
        variables: {
          id: parsedId,
          changed: payload
        }
      });

      console.log('GraphQL mutation result:', result);

      if (result.data?.updatePublisherUser?.message === "Publisher User updated") {
        // Update local state with new photo URL
        // Create a deep copy of the profile to avoid direct state mutation
        const updatedProfile = JSON.parse(JSON.stringify(profile));

        // Update the photoUrl in all possible locations

        // 1. Update in publisheruserSet
        if (updatedProfile.publisheruserSet && updatedProfile.publisheruserSet.length > 0) {
          updatedProfile.publisheruserSet.forEach(pubUser => {
            pubUser.photoUrl = downloadURL;
          });
        }

        // 2. Update in userpublishers
        if (updatedProfile.userpublishers && updatedProfile.userpublishers.length > 0) {
          updatedProfile.userpublishers.forEach(userPub => {
            userPub.photoUrl = downloadURL;
          });
        }

        // 3. Update in publisherusers
        if (updatedProfile.publisherusers && updatedProfile.publisherusers.length > 0) {
          updatedProfile.publisherusers.forEach(pubUser => {
            pubUser.photoUrl = downloadURL;
          });
        }

        // 4. Update the top-level photoUrl
        updatedProfile.photoUrl = downloadURL;

        dispatch(setprofile(updatedProfile));
        setIsUploading(false);
        toaster.success("Profile picture updated successfully");
      } else {
        console.error('Failed to update profile picture:', result.data?.updatePublisherUser?.message);
        toaster.danger('Failed to update profile picture. Please try again later.');
        setIsUploading(false);
      }
    } catch (error) {
      console.error("Error updating profile photo:", error);
      console.error("Error details:", error.message);

      // Log more detailed error information
      if (error.graphQLErrors) {
        console.error("GraphQL errors:", JSON.stringify(error.graphQLErrors, null, 2));

        // Check for specific error types
        const notFoundError = error.graphQLErrors.find(e =>
          e.message.includes("PublisherUser matching query does not exist")
        );

        if (notFoundError) {
          console.error("The PublisherUser record could not be found. This could be because:");
          console.error("1. The ID being used doesn't correspond to an actual PublisherUser");
          console.error("2. The user doesn't have permission to access this PublisherUser");
          console.error("3. The PublisherUser might have been deleted");

          toaster.danger('Profile update failed: User record not found. Please contact support.');
        }
      }

      if (error.networkError) {
        console.error("Network error details:", error.networkError);
      }

      toaster.danger('Failed to update profile picture. Please try again later.');
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      // First update the User model
      const userResult = await updateUser({
        variables: {
          id: parseInt(userId),
          changed: JSON.stringify([
            { key: "firstName", val: firstName },
            { key: "lastName", val: lastName },
            { key: "email", val: email }
          ])
        }
      });

      if (userResult.data?.updateUser?.message === "User Updated") {
        // Now we need to update the PublisherUser model as well

        const possibleIds = [];

        // 1. Check in profile.publisheruserSet (from QueryUser)
        if (profile?.publisheruserSet && profile.publisheruserSet.length > 0) {
          console.log("[AccountSettings] Processing profile.publisheruserSet:", JSON.stringify(profile.publisheruserSet, null, 2));
          for (const pubUser of profile.publisheruserSet) {
            console.log("[AccountSettings] Current pubUser from publisheruserSet:", JSON.stringify(pubUser, null, 2));
            if (pubUser.id) { // Check if PublisherUser ID exists
              if (pubUser.publisher && pubUser.publisher.id) {
                possibleIds.push({
                  id: pubUser.id,
                  source: 'publisheruserSet',
                  publisherId: pubUser.publisher.id
                });
              } else {
                console.warn("[AccountSettings] pubUser from publisheruserSet is missing publisher.id:", JSON.stringify(pubUser, null, 2));
              }
            } else {
              console.warn("[AccountSettings] pubUser from publisheruserSet is missing a top-level id:", JSON.stringify(pubUser, null, 2));
            }
          }
        } else {
          console.log("[AccountSettings] profile.publisheruserSet is empty or undefined.");
        }

        // 2. Check in profile.userpublishers (from GlobalData query)
        if (profile?.userpublishers && profile.userpublishers.length > 0) {
          console.log("[AccountSettings] Processing profile.userpublishers:", JSON.stringify(profile.userpublishers, null, 2));
          for (const userPub of profile.userpublishers) {
            console.log("[AccountSettings] Current userPub from userpublishers:", JSON.stringify(userPub, null, 2));
            if (userPub.id && userPub.publisher && userPub.publisher.id) {
              possibleIds.push({
                id: userPub.id,
                source: 'userpublishers',
                publisherId: userPub.publisher.id
              });
            } else {
              console.warn("[AccountSettings] userPub from userpublishers is missing id or publisher.id:", JSON.stringify(userPub, null, 2));
            }
          }
        } else {
          console.log("[AccountSettings] profile.userpublishers is empty or undefined.");
        }

        // 3. Check in profile.publisherusers (from PublisherUsers query)
        if (profile?.publisherusers && profile.publisherusers.length > 0) {
          for (const pubUser of profile.publisherusers) {
            if (pubUser.id) {
              possibleIds.push({
                id: pubUser.id,
                source: 'publisherusers',
                publisherId: pubUser.publisher?.id
              });
            }
          }
        }

        // 4. Check if the ID is directly in the profile
        if (profile?.publisherUserId) {
          possibleIds.push({
            id: profile.publisherUserId,
            source: 'publisherUserId'
          });
        }

        // 5. Check if the ID is in the global state
        if (allPublishers && allPublishers.length > 0) {
          for (const pubUser of allPublishers) {
            if (pubUser.id) {
              possibleIds.push({
                id: pubUser.id,
                source: 'allpublishers',
                publisherId: pubUser.publisher?.id
              });
            }
          }
        }

        // 6. Hardcoded fallback based on the data you provided
        if (possibleIds.length === 0 && currentpublisher?.id === "1" && userId === "1") {
          possibleIds.push({
            id: "3",  // This is the ID from your provided data
            source: 'hardcoded_fallback',
            publisherId: "1"
          });
        }

        // Log all possible IDs
        console.log('Possible PublisherUser IDs for profile update:', possibleIds);

        // If we have multiple IDs, try to find the one that matches the current publisher
        let selectedId = null;

        if (possibleIds.length > 0) {
          // First, try to find an ID that matches the current publisher
          if (currentpublisher?.id) {
            const matchingId = possibleIds.find(item =>
              item.publisherId && item.publisherId.toString() === currentpublisher.id.toString()
            );

            if (matchingId) {
              selectedId = matchingId.id;
              console.log('Selected ID matching current publisher:', selectedId, 'from source:', matchingId.source);
            }
          }

          // If no matching ID found, just use the first one
          if (!selectedId) {
            selectedId = possibleIds[0].id;
            console.log('Using first available ID:', selectedId, 'from source:', possibleIds[0].source);
          }
        } else {
          console.error('No possible PublisherUser IDs found');
          // Still update the Redux store even if we can't update the PublisherUser
          dispatch(setprofile({
            ...profile,
            firstName,
            lastName,
            email
          }));
          toaster.success("Profile updated in this session, but changes may not persist after refresh");
          setIsEditing(false);
          return;
        }

        // Ensure the ID is an integer
        const parsedId = parseInt(selectedId, 10);
        if (isNaN(parsedId)) {
          console.error('Invalid publisher user ID (not a number):', selectedId);
          // Still update the Redux store even if we can't update the PublisherUser
          dispatch(setprofile({
            ...profile,
            firstName,
            lastName,
            email
          }));
          toaster.success("Profile updated in this session, but changes may not persist after refresh");
          setIsEditing(false);
          return;
        }

        // Use snake_case for backend database column names
        const payload = JSON.stringify([
          { key: "first_name", val: firstName },
          { key: "last_name", val: lastName }
        ]);

        console.log('Updating PublisherUser with ID:', parsedId, 'and payload:', payload);

        // Update the publisher user record
        const pubUserResult = await updatePublisherUser({
          variables: {
            id: parsedId,
            changed: payload
          }
        });

        if (pubUserResult.data?.updatePublisherUser?.message === "Publisher User updated") {
          // Both updates succeeded, update the Redux store
          dispatch(setprofile({
            ...profile,
            firstName,
            lastName,
            email
          }));
          toaster.success("Profile updated successfully");
          setIsEditing(false);
        } else {
          console.error('Failed to update PublisherUser:', pubUserResult);
          // Still update the Redux store even if the PublisherUser update failed
          dispatch(setprofile({
            ...profile,
            firstName,
            lastName,
            email
          }));
          toaster.warning("Profile updated, but changes may not persist after refresh");
          setIsEditing(false);
        }
      } else {
        console.error("Failed to update User:", userResult);
        toaster.danger("Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toaster.danger("Failed to update profile");
    }
  };

  const handlePasswordReset = () => {
    // Add your password reset logic here
  };

  const getProfilePhotoUrl = () => {
    // Try to find a photo URL from various sources
    let photoUrl = null;

    // 1. Try to find a photo URL from the publisheruserSet that matches the current publisher
    if (profile?.publisheruserSet && profile.publisheruserSet.length > 0 && currentpublisher?.id) {
      const matchingPubUser = profile.publisheruserSet.find(pu =>
        pu.publisher && pu.publisher.id.toString() === currentpublisher.id.toString()
      );

      if (matchingPubUser?.photoUrl) {
        photoUrl = matchingPubUser.photoUrl;
        console.log('Using photo URL from matching publisheruserSet:', photoUrl);
        return photoUrl;
      }
    }

    // 2. Try to find a photo URL from userpublishers that matches the current publisher
    if (profile?.userpublishers && profile.userpublishers.length > 0 && currentpublisher?.id) {
      const matchingUserPub = profile.userpublishers.find(up =>
        up.publisher && up.publisher.id.toString() === currentpublisher.id.toString()
      );

      if (matchingUserPub?.photoUrl) {
        photoUrl = matchingUserPub.photoUrl;
        console.log('Using photo URL from matching userpublishers:', photoUrl);
        return photoUrl;
      }
    }

    // 3. Try to find a photo URL from publisherusers that matches the current publisher
    if (profile?.publisherusers && profile.publisherusers.length > 0 && currentpublisher?.id) {
      const matchingPubUser = profile.publisherusers.find(pu =>
        pu.publisher && pu.publisher.id.toString() === currentpublisher.id.toString()
      );

      if (matchingPubUser?.photoUrl) {
        photoUrl = matchingPubUser.photoUrl;
        console.log('Using photo URL from matching publisherusers:', photoUrl);
        return photoUrl;
      }
    }

    // 4. Fall back to the first item in any of these arrays
    if (profile?.publisheruserSet && profile.publisheruserSet.length > 0 && profile.publisheruserSet[0]?.photoUrl) {
      photoUrl = profile.publisheruserSet[0].photoUrl;
      console.log('Using photo URL from first publisheruserSet item:', photoUrl);
      return photoUrl;
    }

    if (profile?.userpublishers && profile.userpublishers.length > 0 && profile.userpublishers[0]?.photoUrl) {
      photoUrl = profile.userpublishers[0].photoUrl;
      console.log('Using photo URL from first userpublishers item:', photoUrl);
      return photoUrl;
    }

    if (profile?.publisherusers && profile.publisherusers.length > 0 && profile.publisherusers[0]?.photoUrl) {
      photoUrl = profile.publisherusers[0].photoUrl;
      console.log('Using photo URL from first publisherusers item:', photoUrl);
      return photoUrl;
    }

    // 5. Finally, fall back to the top-level photoUrl
    if (profile?.photoUrl) {
      photoUrl = profile.photoUrl;
      console.log('Using top-level photo URL:', photoUrl);
      return photoUrl;
    }

    console.log('No photo URL found, returning null');
    return null;
  };

  return (
    <Card
      border="default"
      padding={32}
      backgroundColor="white"
      elevation={1}
    >
      <Paragraph size={500} marginBottom={32}>Account Settings</Paragraph>

      <Pane marginBottom={32}>
        <Pane display="flex" alignItems="center" marginBottom={16}>
          <CloudImage
            avatar={true}
            size={80}
            name={`${firstName} ${lastName}`}
            src={getProfilePhotoUrl()}
            key={`profile-photo-${Date.now()}`}
          />
          <Pane marginLeft={16} width={200}>
            <ImageUpload
              handleImageUpload={handleProfileImageUpdate}
              pathType="profiles"
              itemId={userId ? userId.toString() : ""}
              el="profilePhoto"
            />
          </Pane>
        </Pane>
      </Pane>

      <Pane>
        {isEditing ? (
          <>
            <TextInputField
              label="First Name"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
            />
            <TextInputField
              label="Last Name"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
            />
            <TextInputField
              label="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <Pane display="flex" justifyContent="flex-end" marginTop={16}>
              <Button marginRight={8} onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleSave}>
                Save Changes
              </Button>
            </Pane>
          </>
        ) : (
          <>
            <Pane marginBottom={16}>
              <Paragraph size={300} color="muted">Name</Paragraph>
              <Paragraph size={400}>{firstName} {lastName}</Paragraph>
            </Pane>
            <Pane marginBottom={16}>
              <Paragraph size={300} color="muted">Email</Paragraph>
              <Paragraph size={400}>{email}</Paragraph>
            </Pane>
            <Button onClick={() => setIsEditing(true)}>
              Edit Profile
            </Button>
          </>
        )}
      </Pane>

      <Pane marginTop={32} borderTop paddingTop={16}>
        <Button onClick={handlePasswordReset}>
          Reset Password
        </Button>
      </Pane>

      <Pane marginTop={32} borderTop paddingTop={16}>
        <Text color="red500" size={300} cursor="pointer">
          Delete User Account
        </Text>
      </Pane>
    </Card>
  );
}

export default AccountSettings;
